import moment, { type Moment } from 'moment';
import { Formik } from 'formik';
import classnames from 'classnames';
import React, { useMemo, useState } from 'react';
import { FiAlertCircle, FiCalendar } from 'react-icons/fi';
import { HORIZONTAL_ORIENTATION } from 'react-dates/constants';

import history from 'utils/history';
import * as toast from 'utils/toast';
import { getFormattedDate } from 'utils/date';

import holidaySchema from 'schemas/holiday';

import {
  useCreateHolidayMutation,
  useDeleteHolidayMutation,
  useUpdateHolidayMutation,
} from 'hooks/query/useHolidayQueries';

import * as routes from 'constants/routes';
import { DEFAULT_DATE_FORMAT } from 'constants/date';
import { COUNTRIES, COUNTRIES_FORM_LIST } from 'constants/countries';
import { DEFAULT_HISTORY_ENTRIES, OCCASION_TYPE_IDS, EDIT_PAST_HOLIDAY_NOT_ALLOWED } from 'constants/appConstants';

import LFModal from 'components/common/LFModal';
import PopOver from 'components/common/popover';
import Checkbox from 'components/common/checkbox';
import Loading from 'components/common/loading/Loading';
import DeleteButton from 'components/common/button/DeleteButton';
import DatePicker from 'components/common/datepicker/DatePicker';

interface HolidayFormValues {
  name: string;
  description: string;
  date: Moment;
  countries: string[];
}

interface HolidayPayload {
  name: string;
  description: string;
  date: string;
  type: number;
  countries?: string[];
}

type CountrySelection = Record<string, boolean>;

interface HolidayRecord {
  id: number;
  name: string;
  description: string;
  date: Moment;
  countries: string[] | null;
}

const initialValues: HolidayFormValues = {
  name: '',
  description: '',
  date: moment(),
  countries: [],
};

function getInitialSelectedList(holiday?: HolidayRecord | null): CountrySelection {
  const isEdit = holiday?.id;

  if (!isEdit) {
    return COUNTRIES_FORM_LIST.reduce<CountrySelection>((acc, curr) => {
      acc[curr] = false;
      return acc;
    }, {});
  }

  if (!holiday.countries) {
    return COUNTRIES_FORM_LIST.reduce<CountrySelection>((acc, curr) => {
      acc[curr] = true;
      return acc;
    }, {});
  }

  const { countries } = holiday;
  return COUNTRIES_FORM_LIST.reduce<CountrySelection>((acc, curr) => {
    acc[curr] = countries.includes(curr);
    return acc;
  }, {});
}

interface Props {
  holiday: HolidayRecord | null;
}

const getHolidayInitialValues = (holiday: HolidayRecord | null): HolidayFormValues => {
  if (!holiday) {
    return initialValues;
  }

  return {
    name: holiday.name,
    description: holiday.description,
    date: holiday.date,
    countries: holiday.countries ?? COUNTRIES,
  };
};

const HolidayForm: React.FC<Props> = ({ holiday }) => {
  const createHolidayMutation = useCreateHolidayMutation();
  const updateHolidayMutation = useUpdateHolidayMutation();
  const deleteHolidayMutation = useDeleteHolidayMutation();

  const submitting =
    createHolidayMutation.isPending || updateHolidayMutation.isPending || deleteHolidayMutation.isPending;
  const [selectedCountries, setSelectedCountries] = useState<CountrySelection>(getInitialSelectedList(holiday));

  const isPastHoliday = useMemo(() => {
    const today = getFormattedDate();
    const formattedHolidayDate = getFormattedDate(holiday?.date);
    return Boolean(formattedHolidayDate && formattedHolidayDate < today);
  }, [holiday?.date]);

  const handleCancelClick = () => {
    if (history.length > DEFAULT_HISTORY_ENTRIES) {
      history.goBack();
      return;
    }

    history.push(routes.HOLIDAYS);
  };

  const handleSubmit = (values: HolidayFormValues) => {
    const holidayPayload: HolidayPayload = {
      name: values.name.trim(),
      description: values.description.trim(),
      date: values.date.startOf('day').format(DEFAULT_DATE_FORMAT),
      type: OCCASION_TYPE_IDS.HOLIDAY,
      countries: values.countries,
    };

    if (!values.countries.length || values.countries.length === COUNTRIES.length) {
      delete holidayPayload.countries;
    }

    const onSuccess = () => {
      toast.success({
        title: 'Success',
        message: holiday ? 'Holiday updated successfully.' : 'Holiday created successfully.',
      });

      history.push(routes.HOLIDAYS);
    };

    if (holiday) {
      updateHolidayMutation.mutate(
        { id: holiday.id, holiday: holidayPayload },
        {
          onSuccess,
        }
      );
    } else {
      createHolidayMutation.mutate(holidayPayload, {
        onSuccess,
      });
    }
  };

  const handleHolidayDelete = () => {
    if (!holiday) {
      return;
    }

    deleteHolidayMutation.mutate(holiday.id, {
      onSuccess: () => {
        toast.success({
          title: 'Success',
          message: 'Holiday deleted successfully.',
        });

        if (history.length > DEFAULT_HISTORY_ENTRIES) {
          history.goBack();
          return;
        }

        history.push(routes.HOLIDAYS);
      },
    });
  };

  const handleCountryChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    formikValues: HolidayFormValues,
    setFormikValue: (field: string, value: string[]) => void
  ) => {
    const {
      target: { value, checked },
    } = event;

    if (value === 'All') {
      const allTrueCountries = COUNTRIES_FORM_LIST.reduce<CountrySelection>(
        (acc, curr) => ({ ...acc, [curr]: checked }),
        {}
      );

      setFormikValue('countries', checked ? COUNTRIES : []);
      setSelectedCountries(allTrueCountries);

      return;
    }

    const { countries: currentFormikCountries } = formikValues;

    const updatedValues = checked
      ? [...currentFormikCountries, value]
      : currentFormikCountries.filter((country) => country !== value);

    setFormikValue('countries', updatedValues);

    setSelectedCountries((prev) => {
      const newSelectedCountries = { ...prev, [value]: checked };

      const isAllSelected = Object.entries(newSelectedCountries).every(([key, selected]) => {
        if (key === 'All') {
          return true;
        }

        return selected;
      });

      return { ...newSelectedCountries, All: isAllSelected };
    });
  };

  return (
    <Formik<HolidayFormValues>
      initialValues={getHolidayInitialValues(holiday)}
      validationSchema={holidaySchema}
      onSubmit={handleSubmit}
    >
      {({ handleChange, handleSubmit, touched, errors, values, handleBlur, setFieldValue }) => {
        return (
          <form onSubmit={handleSubmit} className="holiday-form">
            <div className="full-scope-card__content">
              <div className="form-wrap">
                <div className="form-wrap__row form-wrap__row--no-margin clearfix">
                  <div className="form-wrap__col col-sm-6 px-0">
                    <div className="form-group">
                      <label className="form-label dark--text">
                        Title<span className="required ml-5">*</span>
                      </label>
                      <input
                        name="name"
                        type="text"
                        value={values.name}
                        placeholder="Provide a suitable title for the holiday"
                        className={classnames('form-elem form-elem--input-sm', {
                          'form-elem--error': Boolean(errors.name) && Boolean(touched.name),
                        })}
                        onBlur={handleBlur}
                        onChange={handleChange}
                      />
                      {typeof errors.name === 'string' && touched.name && (
                        <div className="error-block">{errors.name}</div>
                      )}
                    </div>

                    <div className="form-group">
                      <label className="form-label dark--text">
                        Description<span className="required ml-5">*</span>
                      </label>
                      <textarea
                        name="description"
                        value={values.description}
                        placeholder="Provide a suitable description for the holiday"
                        className={classnames('form-elem form-elem--input-sm', {
                          'form-elem--error': Boolean(errors.description) && Boolean(touched.description),
                        })}
                        onBlur={handleBlur}
                        onChange={handleChange}
                      />
                      {typeof errors.description === 'string' && touched.description && (
                        <div className="error-block">{errors.description}</div>
                      )}
                    </div>

                    <div className="d-flex flex-direction-responsive">
                      <div className="form-group width--half">
                        <label className="form-label dark--text">
                          Date<span className="required ml-5">*</span>
                        </label>
                        <div className="form-elem--icon-pos">
                          <DatePicker
                            id="date"
                            displayFormat="LL"
                            customInputIcon={<FiCalendar size={18} className="color-grey-50" />}
                            date={values.date}
                            onDateChange={(date) => {
                              const nextDate = moment(date);
                              if (!nextDate.isValid()) {
                                return;
                              }

                              setFieldValue('date', nextDate);
                            }}
                            numberOfMonths={1}
                            orientation={HORIZONTAL_ORIENTATION}
                            isOutsideRange={undefined}
                          />
                        </div>
                        {typeof errors.date === 'string' && touched.date && (
                          <div className="error-block">{errors.date}</div>
                        )}
                      </div>
                    </div>

                    <div className="form-group">
                      <label className="form-label dark--text">
                        Country<span className="required ml-5">*</span>
                      </label>
                      <div className="form-elem--icon-pos">
                        <div className="pl-10">
                          <Checkbox
                            options={COUNTRIES_FORM_LIST.map((country) => ({ label: country, value: country }))}
                            selectedOptions={selectedCountries}
                            onChange={(e) => handleCountryChange(e, values, setFieldValue)}
                          />
                        </div>
                        {typeof errors.countries === 'string' && touched.countries && (
                          <div className="error-block">{errors.countries}</div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="action-bar-footer action-bar-footer--bordered-top px-16">
                <PopOver
                  interactive
                  html={
                    <div className="tooltip-info word-break">
                      <p className="tooltip-info__title">{EDIT_PAST_HOLIDAY_NOT_ALLOWED}.</p>
                    </div>
                  }
                  visible={isPastHoliday}
                >
                  <button
                    type="submit"
                    className={classnames('btn btn--primary btn--curved mr-10', {
                      'holiday-form disable-btn': isPastHoliday,
                      'btn--loading': submitting,
                    })}
                    disabled={submitting || isPastHoliday}
                  >
                    <span className={submitting ? 'invisible' : ''}>Save</span>
                    {submitting && <Loading />}
                  </button>
                </PopOver>
                <button
                  type="button"
                  className={classnames('btn btn--outlined-grey btn--curved mr-10')}
                  disabled={submitting}
                  onClick={handleCancelClick}
                >
                  Cancel
                </button>

                {holiday && !isPastHoliday && (
                  <LFModal
                    TriggerBtn={DeleteButton}
                    modalStatusIcon={<FiAlertCircle color="#F44336" size={24} />}
                    title={{ text: 'Delete this holiday?', type: 'danger' }}
                    className="delete-modal"
                    message={{ text: "You won't be able to revert this!", type: 'delete__text' }}
                    renderFooter={true}
                    buttons={[
                      {
                        text: 'Confirm',
                        type: 'red',
                        className: 'mr-12 delete__btn',
                        onClick: handleHolidayDelete,
                        close: true,
                        showLoading: true,
                      },
                      {
                        text: 'Cancel',
                        type: 'outlined-grey',
                        className: 'py-8 px-12',
                        close: true,
                      },
                    ]}
                  />
                )}
              </div>
            </div>
          </form>
        );
      }}
    </Formik>
  );
};

export default HolidayForm;
